import React, { useEffect, useState } from 'react';
import './CertificationDetail.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Snackbar, Alert, Tab, Tabs } from '@mui/material';
import { motion } from 'framer-motion';
import {
  getCertificationDetail,
  getCertificationModules,
  enrollCertification,
  startApplication,
  createCertificationCheckoutSession,
} from '../../../api/certification';
import { setEnrollment, clearEnrollment } from '../../../Redux/Slices/certificationSlice';
import CertificatePreviewModal from '../CertificatePreviewModal/CertificatePreviewModal';


const BADGE_COLORS = {
  bronze: '#CD7F32',
  silver: '#A8A9AD',
  gold: '#D4AF37',
  platinum: '#6E7F80',
};

function getBadgeColor(level) {
  return BADGE_COLORS[String(level || '').toLowerCase()] || '#4be3cf';
}

function getBadgeIconPath(badgeType) {
  const type = String(badgeType || '').toLowerCase();
  if (type === 'bronze') return '/Assets/Certification/bronze.svg';
  if (type === 'silver') return '/Assets/Certification/silver.svg';
  if (type === 'gold') return '/Assets/Certification/gold.svg';
  if (type === 'platinum') return '/Assets/Certification/platinum.svg';
  return '/Assets/Certification/bronze.svg';
}

export default function CertificationDetail() {
  const { certificationId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const enrollmentMap = useSelector((s) => s.certification.enrollmentMap);
  const localEnrollment = enrollmentMap[certificationId];

  const [cert, setCert] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [enrolling, setEnrolling] = useState(false);
  const [starting, setStarting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [previewCertOpen, setPreviewCertOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'error') =>
    setSnackbar({ open: true, message, severity });

  // Derive current status — prefer live Redux state over stale API response
  const enrollmentStatus = localEnrollment?.status ?? cert?.userEnrollmentStatus ?? null;
  const applicationId = localEnrollment?.applicationId ?? cert?.applicationId ?? null;
  const badgeColor = getBadgeColor(cert?.badgeType ?? cert?.badgeLevel ?? cert?.level);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [detailRes, modulesRes] = await Promise.all([
          getCertificationDetail(certificationId),
          getCertificationModules(certificationId),
        ]);
        const detail = detailRes?.data ?? detailRes;
        const mods = modulesRes?.data ?? modulesRes ?? [];
        setCert(detail);
        setModules(Array.isArray(mods) ? mods : []);

        // Seed or update Redux enrollment map if stale or not present
        if (detail?.userEnrollmentStatus) {
          const statusChanged = !localEnrollment || 
            localEnrollment.status !== detail.userEnrollmentStatus || 
            localEnrollment.applicationId !== detail.applicationId;
            
          if (statusChanged) {
            dispatch(setEnrollment({
              certificationId,
              status: detail.userEnrollmentStatus,
              applicationId: detail.applicationId ?? null,
            }));
          }
        } else {
          // If not enrolled, clear any stale state
          if (localEnrollment) {
            dispatch(clearEnrollment(certificationId));
          }
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) navigate('/signin');
        else if (status === 404) showSnackbar('Certification not found.');
        else showSnackbar(e?.response?.data?.message || 'Failed to load certification.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificationId]);

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const res = await enrollCertification(certificationId);
      const app = res?.data ?? res;
      dispatch(setEnrollment({
        certificationId,
        status: app?.status ?? 'pending',
        applicationId: app?.applicationId ?? app?.id ?? null,
      }));
      
      if (app?.stripeCheckoutUrl) {
        window.location.href = app.stripeCheckoutUrl;
      } else {
        showSnackbar('Enrolled successfully! Click "Start Learning" to begin.', 'success');
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) showSnackbar(e?.response?.data?.message || 'Already enrolled.');
      else if (status === 401) navigate('/signin');
      else showSnackbar(e?.response?.data?.message || 'Enrollment failed.');
    } finally {
      setEnrolling(false);
    }
  };

  const handlePay = async () => {
    const isEnrolled = enrollmentMap[certificationId];
    if (!isEnrolled) return;

    setPaying(true);
    try {
      const successUrl = `${window.location.origin}/payment/success?type=certification&certificationId=${certificationId}&applicationId=${isEnrolled.applicationId ?? ''}`;
      const cancelUrl = `${window.location.origin}/payment/cancel?type=certification&certificationId=${certificationId}&applicationId=${isEnrolled.applicationId ?? ''}`;
      const res = await createCertificationCheckoutSession(certificationId, successUrl, cancelUrl);
      const checkoutUrl = res?.data ?? res;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        showSnackbar('Failed to initiate payment. Please try again.');
      }
    } catch (e) {
      showSnackbar(e?.response?.data?.message || 'Payment initiation failed.');
    } finally {
      setPaying(false);
    }
  };

  const handleStart = async () => {
    if (!applicationId) return;
    setStarting(true);
    try {
      const res = await startApplication(applicationId);
      const app = res?.data ?? res;
      dispatch(setEnrollment({
        certificationId,
        status: app?.status ?? 'in_progress',
        applicationId,
      }));
      navigate(`/certifications/${certificationId}/learn`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) showSnackbar(e?.response?.data?.message || 'Cannot start at this time.');
      else if (status === 401) navigate('/signin');
      else showSnackbar(e?.response?.data?.message || 'Failed to start. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  const renderActionButton = () => {
    const isEnrolled = enrollmentMap[certificationId];
    const price = cert?.price ?? 0;

    if (!isEnrolled) {
      return (
        <button 
          className="cert-det-btn cert-det-btn-enroll"
          disabled={enrolling}
          onClick={handleEnroll}
        >
          {enrolling ? 'Enrolling...' : price > 0 ? `Enroll ($${price})` : 'Enroll Now'}
        </button>
      );
    }

    const { status, applicationId } = isEnrolled;

    if (status === 'unpaid') {
      return (
        <button 
          className="cert-det-btn cert-det-btn-enroll"
          style={{ background: '#ffc107', color: '#111' }}
          disabled={paying}
          onClick={handlePay}
        >
          {paying ? 'Paying...' : 'Pay Now'}
        </button>
      );
    }

    if (status === 'pending') {
      return (
        <button className="cert-det-btn cert-det-btn-start" onClick={handleStart} disabled={starting}>
          {starting ? 'Starting…' : 'Start Learning'}
        </button>
      );
    }
    if (enrollmentStatus === 'in_progress') {
      return (
        <button
          className="cert-det-btn cert-det-btn-exam"
          onClick={() => navigate(`/certifications/${certificationId}/exam/${applicationId}`)}
        >
          Take Exam
        </button>
      );
    }
    if (enrollmentStatus === 'completed') {
      return (
        <button className="cert-det-btn cert-det-btn-view" onClick={() => setPreviewCertOpen(true)}>
          View Certificate
        </button>
      );
    }
    return null;
  };

  // ── Loading skeleton ──
  if (loading) {
    return (
      <div className="cert-det-page">
        <div className="cert-det-container">
          <div className="cert-det-back" onClick={() => navigate('/certifications')}>
            <ArrowBackIcon style={{ fontSize: 18 }} /> Back to Certifications
          </div>
          <div className="cert-det-hero cert-det-hero-skeleton">
            <div className="cert-skeleton cert-skel-badge-lg" />
            <div style={{ flex: 1 }}>
              <div className="cert-skeleton cert-skel-title-lg" />
              <div className="cert-skeleton cert-skel-sub" />
              <div className="cert-skeleton cert-skel-meta-row" />
            </div>
          </div>
          <div className="cert-skeleton cert-skel-body" />
        </div>
      </div>
    );
  }

  if (!cert) return null;

  return (
    <div className="cert-det-page">
      <div className="cert-det-container">
        {/* Back */}
        <button className="cert-det-back" onClick={() => navigate('/certifications')}>
          <ArrowBackIcon style={{ fontSize: 16 }} /> Back to Certifications
        </button>

        {/* Hero card */}
        <motion.div
          className="cert-det-hero"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div
            className="cert-det-badge-icon"
            style={{ background: `${badgeColor}1a`, border: `2px solid ${badgeColor}44` }}
          >
            <img
              src={getBadgeIconPath(cert.badgeType ?? cert.badgeLevel ?? cert.level)}
              alt={`${cert.badgeType ?? cert.badgeLevel ?? cert.level ?? 'Certificate'} Badge`}
              style={{ width: '48px', height: '48px', objectFit: 'contain' }}
            />
          </div>

          <div className="cert-det-hero-body">
            <div className="cert-det-name-row">
              <h1 className="cert-det-name">{cert.name ?? cert.certificationName}</h1>
              {enrollmentStatus === 'completed' && (
                <span className="cert-det-status-pill cert-det-pill-completed">
                  <CheckCircleIcon style={{ fontSize: 14 }} /> Certified
                </span>
              )}
              {enrollmentStatus === 'failed' && (
                <span className="cert-det-status-pill cert-det-pill-failed">Failed</span>
              )}
              {enrollmentStatus === 'expired' && (
                <span className="cert-det-status-pill cert-det-pill-expired">Expired</span>
              )}
            </div>

            <p className="cert-det-description">{cert.description}</p>

            <div className="cert-det-meta-row">
              <span className="cert-det-meta-chip">
                <SchoolIcon style={{ fontSize: 14 }} /> {cert.totalModules ?? 0} Modules
              </span>
              <span className="cert-det-meta-chip">
                <QuizIcon style={{ fontSize: 14 }} /> {cert.totalQuestions ?? 0} Questions
              </span>
              <span className="cert-det-meta-chip cert-det-meta-pass">
                Passing Score: {cert.passingScore ?? 0}%
              </span>
              {(cert.badgeType ?? cert.badgeLevel ?? cert.level) && (
                <span className="cert-det-meta-chip" style={{ color: badgeColor, fontWeight: 600 }}>
                  {(cert.badgeType ?? cert.badgeLevel ?? cert.level).charAt(0).toUpperCase() + (cert.badgeType ?? cert.badgeLevel ?? cert.level).slice(1)} Badge
                </span>
              )}
              <span className="cert-det-meta-chip" style={{ color: cert.price > 0 ? '#ffc107' : '#28a745', fontWeight: 600 }}>
                {cert.price > 0 ? `$${cert.price}` : 'Free'}
              </span>
            </div>

            <div className="cert-det-cta-row">
              {renderActionButton()}
            </div>
          </div>
        </motion.div>

        {/* Tabs: Overview | Modules */}
        <div className="cert-det-tabs-wrap">
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            TabIndicatorProps={{ style: { backgroundColor: '#00d5b9' } }}
            textColor="inherit"
            sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Tab label="Overview" sx={{ color: tab === 0 ? '#00d5b9' : '#9aa3ad', textTransform: 'none', fontWeight: 600 }} />
            <Tab label={`Modules (${modules.length})`} sx={{ color: tab === 1 ? '#00d5b9' : '#9aa3ad', textTransform: 'none', fontWeight: 600 }} />
          </Tabs>

          {/* Overview tab */}
          {tab === 0 && (
            <motion.div
              className="cert-det-tab-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <div className="cert-det-section-card">
                <h3 className="cert-det-section-title">About this Certification</h3>
                <p className="cert-det-section-body">{cert.description || 'No description provided.'}</p>
              </div>

              <div className="cert-det-stats-grid">
                <div className="cert-det-stat">
                  <span className="cert-det-stat-value">{cert.totalModules ?? 0}</span>
                  <span className="cert-det-stat-label">Learning Modules</span>
                </div>
                <div className="cert-det-stat">
                  <span className="cert-det-stat-value">{cert.totalQuestions ?? 0}</span>
                  <span className="cert-det-stat-label">Exam Questions</span>
                </div>
                <div className="cert-det-stat">
                  <span className="cert-det-stat-value" style={{ color: badgeColor }}>{cert.passingScore ?? 0}%</span>
                  <span className="cert-det-stat-label">Passing Score</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Modules tab */}
          {tab === 1 && (
            <motion.div
              className="cert-det-tab-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              {modules.length === 0 ? (
                <div className="cert-det-empty">No modules available yet.</div>
              ) : (
                <div className="cert-det-modules-list">
                  {modules.map((mod, idx) => (
                    <div key={mod.moduleId ?? mod.id ?? idx} className="cert-det-module-row">
                      <div className="cert-det-module-num">{idx + 1}</div>
                      <div className="cert-det-module-info">
                        <div className="cert-det-module-title">{mod.title ?? mod.moduleName}</div>
                        {mod.description && (
                          <div className="cert-det-module-desc">{mod.description}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* CTA if enrolled and in_progress */}
              {enrollmentStatus === 'in_progress' && (
                <div className="cert-det-modules-cta">
                  <button
                    className="cert-det-btn cert-det-btn-exam"
                    onClick={() => navigate(`/certifications/${certificationId}/exam/${applicationId}`)}
                  >
                    Take Exam
                  </button>
                </div>
              )}
              {enrollmentStatus === 'pending' && (
                <div className="cert-det-modules-cta">
                  <button
                    className="cert-det-btn cert-det-btn-start"
                    onClick={handleStart}
                    disabled={starting}
                  >
                    {starting ? 'Starting…' : 'Start Learning'}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      <CertificatePreviewModal
        open={previewCertOpen}
        onClose={() => setPreviewCertOpen(false)}
        certificationId={certificationId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
